import classNames from "classnames";
import { ReactNode, useState } from "react";
import { Controller } from "react-hook-form";

import { FileInput, InputFileUpload, Text } from "@pentabd/ui";

import { fileUpload } from "@api/fileUpload";

import { UploadFileType } from "@type/HRM/upload-file";
import { useLanguage } from "@hooks/useLanguage";
import { CustomFileInfoCardUnique } from "./CustomFileInfoCardUnique";

export type FileDetail = "documentName" | "date" | "remarks" | "memorialNo";

interface FileType {
  id: string;
  downloadButton?: ReactNode;
  fileTypes?: { label?: string; maxSize?: string };
  acceptImage?: boolean;
  noDescription?: boolean;
  customRow?: JSX.Element;
  fileDetails?: FileDetail[];
  [key: string]: any;
}

const fileLabel = ({
  fileTypes,
  noDescription,
  acceptImage,
  language,
}: {
  fileTypes: any;
  noDescription: any;
  acceptImage: any;
  language: any;
}) =>
  fileTypes?.label
    ? fileTypes?.label
    : noDescription
    ? ""
    : language === "en"
    ? acceptImage
      ? "SVG, PNG, JPG, or GIF"
      : "PDF or DOC"
    : acceptImage
    ? "SVG, PNG, JPG, অথবা GIF"
    : "PDF অথবা DOC";

const fileSizeLabel = ({
  fileTypes,
  noDescription,
  acceptImage,
  language,
}: {
  fileTypes: any;
  noDescription: any;
  acceptImage: any;
  language: any;
}) =>
  fileTypes?.maxSize
    ? fileTypes?.maxSize
    : noDescription
    ? ""
    : language === "en"
    ? acceptImage
      ? "max. 1600 x 800px"
      : "max. 10MB"
    : acceptImage
    ? "সর্বোচ্চ ১৬০০ x ৮০০"
    : "সর্বোচ্চ ১০ এমবি";

const NoFileUploaded = () => {
  const { language } = useLanguage();

  return (
    <Text color="subtitle1">
      {language === "en"
        ? "No files have been uploaded"
        : "কোনো ফাইল আপলোড করা হয়নি"}
    </Text>
  );
};

const NoFileAvailable = () => {
  const { language } = useLanguage();

  return (
    <Text color="subtitle1">
      {language === "en" ? "No file available" : "কোন নথি উপলব্ধ নেই"}
    </Text>
  );
};

export const FileUnique = ({
  id,
  control,
  setValue,
  register,
  errors,
  downloadButton,
  disabled,
  fileTypes,
  noDescription,
  acceptImage,
  customRow,
  fileDetails,
  isSingle = true,
}: FileType) => {
  const { language } = useLanguage();

  return (
    <Controller
      control={control}
      name={id}
      render={({ field }) => {
        const file = field.value as UploadFileType;

        const { name: fileName, documentId, fileId, mimeType } = file || {};

        return fileId ? (
          <CustomFileInfoCardUnique
            id={id}
            fileName={fileName}
            fileType={mimeType}
            documentId={documentId}
            fileId={fileId}
            setValue={setValue}
            register={register}
            control={control}
            errors={errors}
            disabled={disabled}
            downloadButton={downloadButton}
            customRow={customRow}
            fileDetails={fileDetails}
            isSingle={isSingle}
          />
        ) : !disabled ? (
          <InputFileUpload
            {...register(id)}
            id={id}
            className="bg-white"
            status="default"
            fileTypesText={fileLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            maxSizeText={fileSizeLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            error={errors as any}
            {...field}
            onChange={async (e: any) => {
              if (e?.target?.files[0]) {
                try {
                  const { data } = await fileUpload(e?.target?.files[0]);
                  const doc = {
                    name: data?.filename,
                    mimeType: data?.fileType,
                    fileId: data?.fileId,
                    documentId: data?.documentId,
                    documentType: id,
                  };

                  setValue(id, doc);
                } catch (error) {
                  setValue(id, null);
                }
              }
            }}
          />
        ) : (
          <NoFileAvailable />
        );
      }}
    />
  );
};

export const SyncFile = ({
  id,
  control,
  setValue,
  register,
  errors,
  addAction,
  removeAction,
  downloadButton,
  disabled,
  fileTypes,
  noDescription,
  acceptImage,
  customRow,
  fileDetails,
  updateSyncFile,
}: FileType) => {
  const [selectedFile, setSelectedFile] = useState({});
  const { language } = useLanguage();

  return (
    <Controller
      control={control}
      name={id}
      render={({ field }) => {
        const file = field.value as UploadFileType;
        setSelectedFile(file);

        const { name: fileName, mimeType, documentId, fileId } = file || {};

        const handleRemove = () => {
          if (removeAction) {
            removeAction(file);
          }
        };

        return fileName ? (
          <CustomFileInfoCardUnique
            id={id}
            fileName={fileName}
            fileType={mimeType}
            documentId={documentId}
            fileId={fileId}
            setValue={setValue}
            register={register}
            control={control}
            errors={errors}
            deleteAction={handleRemove}
            disabled={disabled}
            downloadButton={downloadButton}
            customRow={customRow}
            fileDetails={fileDetails}
            updateSyncFile={updateSyncFile}
            // isSingle
            syncFile
            selectedFile={selectedFile}
          />
        ) : !disabled ? (
          <InputFileUpload
            {...register(id)}
            id={id}
            className="bg-white"
            status="default"
            fileTypesText={fileLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            maxSizeText={fileSizeLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            error={errors as any}
            {...field}
            onChange={async (e: any) => {
              const file = e?.target?.files[0];
              if (file) {
                const fileType = file.type;

                if (acceptImage && !fileType.startsWith("image/")) {
                  alert(
                    language === "en"
                      ? "Please upload an image file."
                      : "দয়া করে একটি ছবি ফাইল আপলোড করুন।",
                  );
                  e.target.value = null;
                  return;
                }

                try {
                  const { data } = await fileUpload(file);

                  const doc = {
                    name: data?.filename,
                    mimeType: data?.fileType,
                    fileId: data?.fileId,
                    documentId: data?.documentId,
                    documentType: id,
                  };

                  addAction(doc);
                } catch (error) {
                  setValue(id, null);
                }
              }
            }}
          />
        ) : (
          <NoFileAvailable />
        );
      }}
    />
  );
};

export const MultiFile = ({
  id,
  control,
  setValue,
  register,
  errors,
  disabled,
  downloadAction,
  fileTypes,
  downloadButton,
  noDescription,
  acceptImage,
  customRow,
  fileDetails,
}: FileType) => {
  const { language } = useLanguage();

  return (
    <Controller
      control={control}
      name={id}
      render={({ field }) => {
        const files = field.value as UploadFileType[];
        const fileArray = Array.isArray(files) ? files : [];

        return (
          <>
            {!disabled && (
              <InputFileUpload
                {...register(id)}
                id={id}
                className="bg-white"
                status="default"
                fileTypesText={fileLabel({
                  fileTypes,
                  noDescription,
                  acceptImage,
                  language,
                })}
                maxSizeText={fileSizeLabel({
                  fileTypes,
                  noDescription,
                  acceptImage,
                  language,
                })}
                error={errors as any}
                multiple
                {...field}
                onChange={async (e: any) => {
                  if (e?.target?.files.length > 0) {
                    try {
                      const newFiles = [];

                      for (let i = 0; i < e.target.files.length; i++) {
                        const file = e.target.files[i];
                        const { data } = await fileUpload(file);

                        const doc = {
                          name: data?.filename,
                          mimeType: data?.fileType,
                          fileId: data?.fileId,
                          documentId: data?.documentId,
                          documentType: id,
                        };

                        newFiles.push(doc);
                      }

                      setValue(id, [...fileArray, ...newFiles]);
                    } catch (error) {
                      console.log(error);
                    }
                  }
                }}
              />
            )}
            {fileArray.length > 0 ? (
              <div
                className={classNames("d-flex flex-column gap-8", {
                  "mt-8": !disabled,
                })}
              >
                {fileArray
                  .sort((a, b) => a.id - b.id)
                  .map((item: UploadFileType) => (
                    <CustomFileInfoCardUnique
                      key={item.id}
                      id={id}
                      fileName={item.name}
                      fileType={item.mimeType}
                      documentId={item.documentId}
                      fileId={item.fileId}
                      setValue={setValue}
                      register={register}
                      control={control}
                      errors={errors}
                      disabled={disabled}
                      downloadAction={downloadAction}
                      downloadButton={downloadButton}
                      fileArray={fileArray}
                      customRow={customRow}
                      fileDetails={fileDetails}
                    />
                  ))}
              </div>
            ) : disabled ? (
              <NoFileUploaded />
            ) : (
              <></>
            )}
          </>
        );
      }}
    />
  );
};

export const SyncMultiFile = ({
  id,
  control,
  setValue,
  register,
  errors,
  disabled,
  downloadAction,
  addAction,
  removeAction,
  downloadButton,
  fileTypes,
  noDescription,
  acceptImage,
  customRow,
  fileDetails,
  updateSyncFile,
}: FileType) => {
  const [selectedFile, setSelectedFile] = useState([]);

  const { language } = useLanguage();

  return (
    <Controller
      control={control}
      name={id}
      render={({ field }) => {
        const files = field.value as UploadFileType[];
        setSelectedFile(files as any);
        const fileArray = Array.isArray(files) ? files : [];

        return (
          <>
            {!disabled && (
              <InputFileUpload
                {...register(id)}
                id={id}
                className="bg-white"
                status="default"
                fileTypesText={fileLabel({
                  fileTypes,
                  noDescription,
                  acceptImage,
                  language,
                })}
                maxSizeText={fileSizeLabel({
                  fileTypes,
                  noDescription,
                  acceptImage,
                  language,
                })}
                error={errors as any}
                multiple
                {...field}
                onChange={async (e: any) => {
                  if (e?.target?.files.length > 0) {
                    try {
                      const newFiles = [];

                      for (let i = 0; i < e.target.files.length; i++) {
                        const file = e.target.files[i];
                        const { data } = await fileUpload(file);

                        const doc = {
                          id: data?.id,
                          name: data?.filename,
                          mimeType: data?.fileType,
                          fileId: data?.fileId,
                          documentId: data?.documentId,
                          documentType: id,
                        };

                        newFiles.push(doc);
                      }

                      setValue(id, [...fileArray, ...newFiles]);
                      addAction(newFiles);
                    } catch (error) {
                      console.log(error);
                    }
                  }
                }}
              />
            )}
            {fileArray.length > 0 ? (
              <div
                className={classNames("d-flex flex-column gap-8", {
                  "mt-8": !disabled,
                })}
              >
                {fileArray
                  .sort((a, b) => a.id - b.id)
                  .map((item: UploadFileType) => (
                    <CustomFileInfoCardUnique
                      id={id}
                      key={item.id}
                      itemId={item.id}
                      fileName={item.name}
                      fileType={item.mimeType}
                      documentId={item.documentId}
                      fileId={item.fileId}
                      setValue={setValue}
                      register={register}
                      control={control}
                      errors={errors}
                      disabled={disabled}
                      downloadAction={downloadAction}
                      downloadButton={downloadButton}
                      fileArray={fileArray}
                      deleteAction={removeAction}
                      customRow={customRow}
                      fileDetails={fileDetails}
                      updateSyncFile={updateSyncFile}
                      selectedFile={selectedFile}
                      syncMultiFile
                    />
                  ))}
              </div>
            ) : disabled ? (
              <NoFileUploaded />
            ) : (
              <></>
            )}
          </>
        );
      }}
    />
  );
};

export const InputFile = ({
  id,
  control,
  setValue,
  register,
  errors,
  downloadButton,
  disabled,
  fileTypes,
  noDescription,
  acceptImage,
}: FileType) => {
  const { language } = useLanguage();

  return (
    <Controller
      control={control}
      name={id}
      render={({ field }) => {
        const file = field.value as UploadFileType;
        const { name: fileName, documentId, fileId, mimeType } = file || {};

        return fileName ? (
          <CustomFileInfoCardUnique
            id={id}
            fileName={fileName}
            fileType={mimeType}
            documentId={documentId}
            fileId={fileId}
            setValue={setValue}
            register={register}
            errors={errors}
            disabled={disabled}
            downloadButton={downloadButton}
          />
        ) : (
          <FileInput
            {...register(id)}
            id={id}
            className="bg-white"
            status="default"
            fileTypesText={fileLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            maxSizeText={fileSizeLabel({
              fileTypes,
              noDescription,
              acceptImage,
              language,
            })}
            error={errors as any}
            {...field}
            onChange={async (e: any) => {
              if (e?.target?.files[0]) {
                try {
                  const { data } = await fileUpload(e?.target?.files[0]);
                  const doc = {
                    name: data?.filename,
                    mimeType: data?.fileType,
                    fileId: data?.fileId,
                    documentId: data?.documentId,
                    documentType: id,
                  };

                  setValue(id, doc);
                } catch (error) {
                  setValue(id, null);
                }
              }
            }}
          />
        );
      }}
    />
  );
};
